import SpineAnimation from "@base/spineAnimation/SpineAnimation";
import * as PIXI from "pixi.js"
import BaseContainer from "@base/container/BaseContainer";
import Utils from "@base/utils/Utils";

export default class ParallaxLayer extends BaseContainer {

  constructor(layerSettings, {speed}) {
    super()

    this.settings = layerSettings
    this.name = this.settings.name
    this.speed = speed
    this.totalWidth = 0

    this.animations = []
    this.displayObjects = []

    this.init(this.settings)
  }

  init(layerSettings) {
    this.createLayer(layerSettings)
  }

  createLayer(layerSettings) {
    const {name, objects} = layerSettings

    this.settings = layerSettings
    this.name = name
    this.prevChild = null // To calculate gaps

    if (layerSettings.name === 'Front layer with front mountains') {
      const colorMatrix = new PIXI.filters.ColorMatrixFilter()
      colorMatrix.brightness(0.2, false)
      this.filters = [colorMatrix]
    }

    // Dublicate whole objects to jump to it to make infinite cycle in parallax
    let objectsWithCycle = [...objects]

    // In the fixed width (in our case) we don't need to dublicate layer
    if (!layerSettings.layerWidth) {
      objectsWithCycle = [...objects, ...objects]
    }

    // // In this case, all objects will be combined with comb method
    // if (layerSettings.mirrored) {
    //   let objects1 = [...objects];
    //   let objects2 = [...objects];
    //
    //   // Comb objects mixing -> [1,2,3] + [4,5,6] = [1,4,2,5,3,6]
    //   objectsWithCycle = objects1.reduce((prev, curr, index) => [...prev, curr, objects2[index]], [])
    // }

    objectsWithCycle.forEach((objectSettings, i, arr) => {
      const displayObject = this.createItem(objectSettings, layerSettings)

      this.totalWidth += displayObject.width + (layerSettings.gap || 0)

      this.addChild(displayObject)

      // To use it inside the arrangement
      this.prevChild = displayObject

      this.displayObjects.push(displayObject)
    })

    // Correct the case, when duplicate objects use random range Y coordinate position.
    const {displayObjects} = this

    this.totalWidth /= 2 // Remove duplicate size

    // When we obviously sets the layer width
    if (layerSettings.hasOwnProperty('layerWidth')) {
      this.totalWidth = layerSettings.layerWidth
    } else {
      // In the fixed width (in our case) we don't need to dublicate layer
      const halfLength = (displayObjects.length / 2)
      for (let i = 0; i < halfLength; ++i) {
        const objSource = displayObjects[i]
        const objCopy = displayObjects[i + halfLength]
        objCopy.y = objSource.y
      }
    }

    /*
    // Debug bounds
    const a = new PIXI.Graphics()
    a.beginFill(0xff0000)
    a.drawRect(0,0,3,this.height)
    a.endFill()
    this.addChild(a)

    const b = new PIXI.Graphics()
    b.beginFill(0x00ff00)
    b.drawRect(0.5 * this.width-3,0,3,this.height)
    b.endFill()
    this.addChild(b)

    const c = new PIXI.Graphics()
    c.beginFill(0x0000ff, 0.2)
    c.drawRect(0,0,this.width,this.height)
    c.endFill()
    this.addChildAt(c, 0)
    */
  }

  createItem(objectSettings, layerSettings) {
    let resultObject = null

    switch (layerSettings.type) {
      case 'animation': {
        const {name} = objectSettings

        const animation = new SpineAnimation(name)
        animation.name = name
        animation.settings = objectSettings
        animation.type = layerSettings.type
        this.animations.push(animation)

        if (animation.settings?.repeat) {
          this.animateIdle()
        } else {
          // TODO: Correct animation invoking is here:
          this.animateIdle()
          this.timerID = window.setInterval(() => this.animateStart(), 5000)
        }

        resultObject = animation
      }
        break

      case 'image':
      default: {
        const {name} = objectSettings
        const image = new PIXI.Sprite(PIXI.Texture.from(name))
        image.settings = objectSettings
        image.type = layerSettings.type
        image.name = name

        resultObject = image
      }
    }

    if ('scale' in objectSettings && objectSettings.scale != 1) {
      resultObject.scale.set(objectSettings.scale);
    }

    if ('width' in objectSettings)
      resultObject.width = objectSettings.width
    if ('height' in objectSettings)
      resultObject.height = objectSettings.height

    resultObject.num = this.children.length

    this.applyGap(resultObject, layerSettings)
    this.applyOffsetAndPosition(resultObject)

    return resultObject
  }

  applyGap(resultObject, layerSettings) {
    const {gap, gapOrientation} = layerSettings

    if (layerSettings.hasOwnProperty('gap')) {
      if (this.children.length === 0) {
        resultObject.position.set(gap || 0, 0)
      } else {
        if (!gapOrientation || gapOrientation.includes('h')) {
          const prevGapAndSize = this.prevChild.width + this.prevChild.x
          resultObject.position.set((gap || 0) + prevGapAndSize, 0)
        }
      }
    }
  }

  applyOffsetAndPosition(object) {
    const {settings} = object

    let offsetX = 0
    let offsetY = 0

    // Checks range like '100-400`
    const getRange = (offset) => {
      if (typeof offset === 'string' && offset.includes('-')) {
        const range = offset.split('-')
        if (range && range.length === 2) {
          const [min, max] = range
          return {
            min: parseInt(min),
            max: parseInt(max)
          }
        }
      }

      return null
    }

    if (settings.x) {
      const range = getRange(settings.x)
      if (range) {
        offsetX = Math.round(range.min + Math.random() * (range.max - range.min))
      } else {
        offsetX = settings.x
      }
    }

    if (settings.y) {
      const range = getRange(settings.y)
      if (range) {
        offsetY = Math.round(range.min + Math.random() * (range.max - range.min))
      } else {
        offsetY = settings.y
      }
    }

    object.x += offsetX
    object.y += offsetY
  }

  move(multiplier) {
    this.x -= (this.speed * this.settings.speedMultiplier) * (multiplier ?? 1)

    // Restore the layer position
    if (this.x < -(this.totalWidth)) {
      this.reset()
    }
  }

  reset() {
    this.x = 0
  }

  animateIdle() {
    this.animations.forEach(anim => {
      const {animations} = anim.settings
      anim.start([animations[0]], [true])
    })
  }

  animateStart() {
    this.animations.forEach(anim => {
      const {animations} = anim.settings
      const withoutFirst = animations.length > 1 ? anim.settings.animations.slice(1) : animations
      const shuffled = Utils.shuffle(withoutFirst)
      anim.start(shuffled, shuffled.map(() => false))
    })
  }

  animateStop() {
    this.animations.forEach(anim => {
      anim.stop()
    })

    window.clearInterval(this.timerID)
  }
}
